/* ---------------------------------------------------------------------------
   The record's 3D canvas toolbar, and the camera arithmetic behind it.

   THE MODELLER'S OWN SET, docked the way the modeller docks it: a command bar
   across the full width at the top of the canvas (`#modeler-cmdbar` in
   vendor/bridge-modeler/index.html) holding Zoom In, Zoom Out, Zoom All, the six
   standard views, the projection switch and Set Orbit Point. The labels and titles
   are the modeller's. Left out, as the Defect Inspector's toolbar leaves them out:
   the 2D outline mode (it needs the Canvas2DRenderer and a DXF writer), the
   settings flyout (quality, textures and scenery belong to a model being built,
   not to one being looked at) and isolate-selection (there is no selection on the
   record to isolate).

   ONE COMMAND IS THE REGISTER'S, NOT THE MODELLER'S: Log defect, at the end of the
   bar. It borrows the next click as Set Orbit Point does, and ./meshView.ts turns
   that click into the "Log defect" dialog opened at the point on the model — see
   `DefectCommands`.

   THE ARITHMETIC IS THE MODELLER'S TOO, by way of src/inspector/scene.ts, which
   ported it first and carries the full account of each rule: the ortho camera is
   derived from the perspective one every frame (`Canvas3D.SyncOrthographicCamera`),
   an axial view is fitted to the silhouette rather than the box
   (`OrthoAxialFitDistance`), and a zoom step is a fifth in and a quarter out
   (`Canvas3D.ZoomIn` / `ZoomOut`). Two things differ from the modeller and are the
   Inspector's choices, kept here so the two views of one `.glb` behave alike:
   which way the bridge runs is MEASURED rather than assumed to be +X, because a
   CSiBridge export runs along whichever axis the source used; and a standard view
   tilts the camera a hair off the axis instead of rotating its up vector and
   locking orbit, so every view is somewhere an ordinary drag can leave and come
   back to.

   BUILT ONCE AND CORRECTED IN PLACE, not drawn by src/core/dom.ts. It lives inside
   the canvas's host, which is parked on document.body outside the render root
   (see ./meshView.ts for why a WebGL canvas cannot be reconciled), so the buttons
   are made here, held, and brought up to date by `refresh()`.

   THREE.JS IS PASSED IN, never imported, as in ./framing.ts: it is a ~600 kB
   on-demand chunk and a static import here would drag it into the register bundle.
   --------------------------------------------------------------------------- */

import { icon, ICONS, type IconPath } from '../core/dom.ts';

/** The six axial views, by the modeller's own names (`StandardOrthoView`). */
export type StandardView = 'top' | 'bottom' | 'front' | 'back' | 'left' | 'right';

export type Projection = 'perspective' | 'orthographic';

/** Everything the toolbar may ask of the camera. Getters are re-read on refresh. */
export interface ViewCommands {
  zoomIn(): void;
  zoomOut(): void;
  /** Frame the whole structure — along the current axis if one is chosen. */
  zoomAll(): void;
  standardView(view: StandardView): void;
  /** Which of the six the camera is still on, or null once it has been moved. */
  activeView(): StandardView | null;
  projection(): Projection;
  setProjection(mode: Projection): void;
  /** Arm the next click to set the orbit point instead of orbiting. */
  armOrbitPoint(on: boolean): void;
  orbitPointArmed(): boolean;
  /** Whether there is geometry on screen to act on. */
  ready(): boolean;
}

/**
 * Logging a defect on the model — the record's own command, not the modeller's.
 *
 * Separate from `ViewCommands` because it is not about the camera: it borrows the
 * next click the way Set Orbit Point does, but what it does with it is open the
 * register's "Log defect" dialog at the point clicked. Optional, so a bar built
 * for looking only (a check, a future read-only view) simply has no such button.
 */
export interface DefectCommands {
  /** Arm the next click on the model to place a defect there. */
  armPlace(on: boolean): void;
  placeArmed(): boolean;
  /** Null where this person may log a defect, else why they may not. */
  refusal(): string | null;
}

/**
 * Playing the construction record back on the model — Construction
 * Monitoring's command, as Log defect is Bridge Inspection's. See ./buildSim.ts.
 */
export interface SimCommands {
  toggle(): void;
  running(): boolean;
  /** Null where there is a record to play, else why not, for the button's title. */
  refusal(): string | null;
  /** What pressing it plays, for the title: the record, or the programme. */
  idleTitle(): string;
}

/**
 * The ground around the structure, in this canvas: the land within a radius,
 * with its contours — see src/map/isolate.ts. Optional like the others.
 */
export interface TerrainCommands {
  toggle(): void;
  shown(): boolean;
  /** Loading, so the button can say so. */
  busy(): boolean;
  radiusKm(): number;
  setRadiusKm(km: number): void;
  /** Null where the ground can be drawn, else why not. */
  refusal(): string | null;
}

/** Radii offered in the picker, km. Mirrors ISOLATE_RADII_KM without importing the map. */
const TERRAIN_RADII_KM = [1, 2, 5, 10];

export interface ViewToolbar {
  refresh(): void;
  destroy(): void;
}

/** Height of the bar, in px. The canvas is laid out below it. */
export const TOOLBAR_H = 36;

/** The six, in the modeller's toolbar order, with its own titles. */
const VIEWS: Record<StandardView, { label: string; title: string }> = {
  top: { label: 'Top', title: 'Top View (looking down)' },
  bottom: { label: 'Bottom', title: 'Bottom View (looking up)' },
  front: { label: 'Front', title: 'Front View (long elevation)' },
  back: { label: 'Back', title: 'Back View (opposite long elevation)' },
  left: { label: 'Left', title: 'Left View (cross-section at start abutment)' },
  right: { label: 'Right', title: 'Right View (cross-section at end abutment)' },
};

const STANDARD_VIEWS = Object.keys(VIEWS) as StandardView[];

/* The icon is the state and the title is the action — App.SyncCameraViewButtonState. */
const PROJECTION: Record<Projection, { path: IconPath; title: string }> = {
  perspective: { path: ICONS.cube, title: 'Switch to Orthographic' },
  orthographic: { path: ICONS.ortho, title: 'Switch to Perspective' },
};

function toolButton(title: string, path: IconPath, press: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'mtool';
  b.title = title;
  b.setAttribute('aria-label', title);
  b.appendChild(icon(path, { size: 16, width: 1.8 }));
  b.addEventListener('click', press);
  return b;
}

function separator(): HTMLElement {
  const s = document.createElement('span');
  s.className = 'mtools__sep';
  s.setAttribute('aria-hidden', 'true');
  return s;
}

/** Build the bar into the top of `host` and wire it to the camera. */
export function mountViewToolbar(
  host: HTMLElement,
  cmd: ViewCommands,
  defects?: DefectCommands,
  sim?: SimCommands,
  terrain?: TerrainCommands,
): ViewToolbar {
  const bar = document.createElement('div');
  bar.className = 'mtools';
  bar.setAttribute('role', 'toolbar');
  bar.setAttribute('aria-label', 'Viewport commands');

  const zoomIn = toolButton('Zoom In', ICONS.zoomIn, () => cmd.zoomIn());
  const zoomOut = toolButton('Zoom Out', ICONS.zoomOut, () => cmd.zoomOut());
  const zoomAll = toolButton('Zoom All', ICONS.fit, () => cmd.zoomAll());

  const views = document.createElement('div');
  views.className = 'mtools__views';
  views.setAttribute('role', 'group');
  views.setAttribute('aria-label', 'Standard views');
  const viewBtns = new Map<StandardView, HTMLButtonElement>();
  for (const v of STANDARD_VIEWS) {
    const b = document.createElement('button');
    b.type = 'button';
    b.className = 'mtools__view';
    b.title = VIEWS[v].title;
    b.textContent = VIEWS[v].label;
    b.setAttribute('aria-pressed', 'false');
    b.addEventListener('click', () => cmd.standardView(v));
    views.appendChild(b);
    viewBtns.set(v, b);
  }

  const projBtn = toolButton(PROJECTION.perspective.title, PROJECTION.perspective.path, () =>
    cmd.setProjection(cmd.projection() === 'perspective' ? 'orthographic' : 'perspective'),
  );
  const orbitBtn = toolButton(
    'Set Orbit Point — click a point in the scene',
    ICONS.orbitPoint,
    () => cmd.armOrbitPoint(!cmd.orbitPointArmed()),
  );
  orbitBtn.setAttribute('aria-pressed', 'false');

  bar.append(zoomIn, zoomOut, zoomAll, separator(), views, separator(), projBtn, orbitBtn);

  /* LOG DEFECT, labelled where the rest are icons: it is the one command on the
     bar that records something, and a triangle among the zoom buttons would read
     as a warning about the view. Last and after a rule, as a different kind of
     action from the camera's. */
  let logBtn: HTMLButtonElement | null = null;
  if (defects) {
    logBtn = document.createElement('button');
    logBtn.type = 'button';
    logBtn.className = 'mtools__view mtools__log';
    logBtn.append(icon(ICONS.alert, { size: 14, width: 1.8 }), document.createTextNode('Log inspection'));
    logBtn.setAttribute('aria-pressed', 'false');
    logBtn.addEventListener('click', () => defects.armPlace(!defects.placeArmed()));
    const rule = separator();
    rule.classList.add('mtools__sep--log');
    bar.append(rule, logBtn);
  }

  /* SIMULATE, labelled for the reason Log defect is: it is not a camera command.
     Shown only in Construction Monitoring and Scheduler — see `.det-live--simulates`. */
  let simBtn: HTMLButtonElement | null = null;
  if (sim) {
    simBtn = document.createElement('button');
    simBtn.type = 'button';
    simBtn.className = 'mtools__view mtools__sim';
    simBtn.append(icon('M7 4.5v15l12-7.5z', { size: 14, width: 1.8 }), document.createTextNode('Simulate'));
    simBtn.setAttribute('aria-pressed', 'false');
    simBtn.addEventListener('click', () => sim.toggle());
    const rule = separator();
    rule.classList.add('mtools__sep--sim');
    bar.append(rule, simBtn);
  }
  /* TERRAIN: the land within a radius of the structure, under the model. Labelled
     like the two above, with the radius beside it. */
  let terrainBtn: HTMLButtonElement | null = null;
  let radiusSel: HTMLSelectElement | null = null;
  if (terrain) {
    terrainBtn = document.createElement('button');
    terrainBtn.type = 'button';
    terrainBtn.className = 'mtools__view mtools__terrain';
    terrainBtn.append(icon(ICONS.map, { size: 14, width: 1.8 }), document.createTextNode('Terrain'));
    terrainBtn.setAttribute('aria-pressed', 'false');
    terrainBtn.addEventListener('click', () => terrain.toggle());
    radiusSel = document.createElement('select');
    radiusSel.className = 'mtools__radius';
    radiusSel.title = 'Radius of ground shown around the structure';
    radiusSel.setAttribute('aria-label', 'Terrain radius');
    for (const km of TERRAIN_RADII_KM) {
      const o = document.createElement('option');
      o.value = String(km);
      o.textContent = `${km} km`;
      radiusSel.appendChild(o);
    }
    radiusSel.addEventListener('change', () => terrain.setRadiusKm(Number(radiusSel!.value)));
    const rule = separator();
    rule.classList.add('mtools__sep--terrain');
    bar.append(rule, terrainBtn, radiusSel);
  }
  host.prepend(bar);

  const refresh = (): void => {
    const ready = cmd.ready();
    for (const b of [zoomIn, zoomOut, zoomAll, projBtn, orbitBtn]) b.disabled = !ready;

    const mode = cmd.projection();
    projBtn.replaceChildren(icon(PROJECTION[mode].path, { size: 16, width: 1.8 }));
    projBtn.title = PROJECTION[mode].title;
    projBtn.setAttribute('aria-label', PROJECTION[mode].title);
    projBtn.classList.toggle('is-active', mode === 'orthographic');

    const armed = cmd.orbitPointArmed();
    orbitBtn.classList.toggle('is-active', armed);
    orbitBtn.setAttribute('aria-pressed', String(armed));
    host.classList.toggle('det-live--arming', armed);

    if (defects && logBtn) {
      const refusal = defects.refusal();
      const placing = defects.placeArmed();
      logBtn.disabled = !ready || refusal !== null;
      logBtn.title =
        refusal ??
        (placing
          ? 'Click the model where the issue was seen — Esc to cancel'
          : 'Log inspection — click the point on the model where the issue was seen');
      logBtn.classList.toggle('is-active', placing);
      logBtn.setAttribute('aria-pressed', String(placing));
      host.classList.toggle('det-live--placing', placing);
    }

    if (sim && simBtn) {
      const refusal = sim.refusal();
      const running = sim.running();
      simBtn.disabled = !running && (!ready || refusal !== null);
      simBtn.title = running
        ? 'Stop the playback and show the model as drawn'
        : (refusal ?? sim.idleTitle());
      simBtn.classList.toggle('is-active', running);
      simBtn.setAttribute('aria-pressed', String(running));
    }

    if (terrain && terrainBtn && radiusSel) {
      const refusal = terrain.refusal();
      const on = terrain.shown();
      const busy = terrain.busy();
      terrainBtn.disabled = !ready || refusal !== null || busy;
      terrainBtn.title =
        refusal ??
        (busy
          ? 'Reading the ground…'
          : on
            ? 'Hide the ground'
            : 'Show the ground around the structure, with its contours');
      terrainBtn.classList.toggle('is-active', on);
      terrainBtn.setAttribute('aria-pressed', String(on));
      radiusSel.value = String(terrain.radiusKm());
      radiusSel.disabled = !ready || refusal !== null || busy;
    }

    const active = cmd.activeView();
    for (const [v, b] of viewBtns) {
      b.disabled = !ready;
      const on = v === active;
      b.classList.toggle('is-active', on);
      b.setAttribute('aria-pressed', String(on));
    }
  };

  refresh();
  return {
    refresh,
    destroy: () => {
      host.classList.remove('det-live--arming', 'det-live--placing');
      bar.remove();
    },
  };
}

/* --------------------------- camera arithmetic --------------------------- */

/**
 * How far off the axis a "straight down" camera sits, as a fraction of the
 * viewing distance. A camera looking exactly along its up vector has no defined
 * orientation; nudging it here, in a known direction, decides which way the
 * bridge lies on screen. See AXIAL_TILT in src/inspector/scene.ts.
 */
const AXIAL_TILT = 1e-3;

/**
 * Where to stand for one of the six, and how big the silhouette is from there.
 * Which way the bridge runs is measured off the box: the longer horizontal
 * extent is "along", so Front is always the long elevation.
 */
export function axialFrame(
  three: typeof import('three'),
  box: import('three').Box3,
  view: StandardView,
): { centre: import('three').Vector3; eye: import('three').Vector3; sx: number; sy: number } {
  const centre = box.getCenter(new three.Vector3());
  const size = box.getSize(new three.Vector3());

  const alongX = size.x >= size.z;
  const along = new three.Vector3(alongX ? 1 : 0, 0, alongX ? 0 : 1);
  const across = new three.Vector3(alongX ? 0 : 1, 0, alongX ? 1 : 0);
  const length = Math.max(alongX ? size.x : size.z, 0.1);
  const width = Math.max(alongX ? size.z : size.x, 0.1);
  const height = Math.max(size.y, 0.1);

  const up = new three.Vector3(0, 1, 0);
  const tilt = across.clone().multiplyScalar(AXIAL_TILT);

  switch (view) {
    case 'top':
      return { centre, eye: up.clone().add(tilt), sx: length, sy: width };
    case 'bottom':
      return { centre, eye: up.clone().negate().add(tilt), sx: length, sy: width };
    case 'front':
      return { centre, eye: across.clone(), sx: length, sy: height };
    case 'back':
      return { centre, eye: across.clone().negate(), sx: length, sy: height };
    case 'left':
      return { centre, eye: along.clone().negate(), sx: width, sy: height };
    case 'right':
      return { centre, eye: along.clone(), sx: width, sy: height };
  }
}

/**
 * The distance at which a silhouette of `sx × sy` just fits the view — the
 * modeller's `OrthoAxialFitDistance`, with its 1.06 margin.
 */
export function axialDistance(sx: number, sy: number, fovDeg: number, aspect: number): number {
  const tanHalf = Math.tan((fovDeg * Math.PI) / 360);
  if (tanHalf <= 1e-6) return 100;
  const fromHeight = sy / 2 / tanHalf;
  const fromWidth = sx / 2 / (aspect * tanHalf);
  return Math.max(fromHeight, fromWidth) * 1.06;
}

/**
 * Copy the perspective camera onto the orthographic one —
 * `Canvas3D.SyncOrthographicCamera`. The frustum is sized at the orbit target's
 * distance, where the two projections agree, so switching leaves what is at the
 * target the same size on screen.
 */
export function syncOrtho(
  ortho: import('three').OrthographicCamera,
  camera: import('three').PerspectiveCamera,
  distance: number,
): void {
  ortho.position.copy(camera.position);
  ortho.quaternion.copy(camera.quaternion);
  ortho.up.copy(camera.up);
  const halfH = distance * Math.tan((camera.fov * Math.PI) / 360);
  const halfW = halfH * camera.aspect;
  ortho.left = -halfW;
  ortho.right = halfW;
  ortho.top = halfH;
  ortho.bottom = -halfH;
  ortho.near = camera.near;
  ortho.far = camera.far;
  ortho.updateProjectionMatrix();
  ortho.updateMatrixWorld();
}
